import type { QuoteCatalogItem, ResellerCustomer } from "@xop-any-time/shared";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Plus, Trash2 } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { ApiError, isPermissionDenied } from "../../../lib/api.js";
import { keys, listCustomers } from "../../../lib/crmApi.js";
import { requestXopQuote } from "../../../lib/xopQuotesApi.js";
import { fetchQuoteCatalog } from "../priceList.js";
import { PageHeader } from "../crm.js";
import {
  MAX_QUANTITY,
  blankLine,
  categoryLabelFor,
  orderByCategory,
  submitBlocker,
  toRequestItems,
  unitLabelFor,
  type QuoteLineDraft,
} from "./requestQuote.js";

/**
 * "Tell XOP Networks what you need" — a REQUEST, not a quote (2026-09-13).
 *
 * Two questions and nothing else: WHO is it for, and WHAT do you need. The
 * reseller picks one of its own customers, then adds item lines — choose the
 * thing, the page shows the unit it is counted in, type how many.
 *
 * THERE ARE NO PRICES ON THIS PAGE, and not because they are hidden: the
 * catalogue it reads (`/quote-catalog`) has no money in the payload at all.
 * A reseller says what it needs; XOP says what that costs. A price here is a
 * price a reseller could quote onward before XOP has agreed to it.
 *
 * Pressing Generate quote hands the request to crm-api, which prices every
 * line off the command-center catalogue, numbers it, stamps THIRTY DAYS —
 * always, and nothing on this page offers to change it — saves it to this
 * reseller's quote list, mirrors it into the command center's, and tells every
 * XOP super admin. The priced document is what the next page shows, and it is
 * the server's: the only thing the reseller decides is what it asked for.
 */

function LineRow({
  line,
  catalog,
  disabled,
  onChange,
  onRemove,
  canRemove,
  index,
}: {
  line: QuoteLineDraft;
  catalog: QuoteCatalogItem[];
  disabled: boolean;
  onChange: (next: QuoteLineDraft) => void;
  onRemove: () => void;
  canRemove: boolean;
  index: number;
}): JSX.Element {
  const selected = catalog.find((i) => i.id === line.itemId) ?? null;
  const unit = unitLabelFor(selected ?? undefined);

  return (
    <Box
      data-testid={`quote-line-${index}`}
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) 150px 40px" },
        alignItems: "start",
      }}
    >
      <Autocomplete
        size="small"
        disabled={disabled}
        options={catalog}
        value={selected}
        groupBy={(o) => categoryLabelFor(o.category)}
        getOptionLabel={(o) => o.description}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        onChange={(_e, option) => onChange({ ...line, itemId: option?.id ?? null })}
        renderOption={(props, o) => {
          const { key: _key, ...liProps } = props as { key?: string };
          return (
            <Box component="li" key={`item-${o.id}`} {...liProps}>
              <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                  {o.description}
                </Box>
                <Box component="span" sx={{ fontSize: 11.5, color: "text.secondary" }}>
                  {o.part_number} · {unitLabelFor(o)}
                </Box>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          // AutocompleteRenderInputParams doesn't line up with TextFieldProps
          // under exactOptionalPropertyTypes — narrow it (valid at runtime),
          // the same way the ported CRM's own pickers do.
          <TextField
            {...(params as TextFieldProps)}
            label={`Item ${index + 1}`}
            placeholder="Search what you need…"
            inputProps={{ ...params.inputProps, "aria-label": `Item ${index + 1}` }}
          />
        )}
      />

      <TextField
        size="small"
        type="number"
        label={unit === "" ? "Quantity" : `Quantity (${unit})`}
        value={line.quantity}
        disabled={disabled || selected === null}
        // The RAW text, not a clamped number — clamping on every keystroke
        // made the field impossible to clear and retype (see QuoteLineDraft).
        // `toRequestItems` is where it becomes a number.
        onChange={(e) => onChange({ ...line, quantity: e.target.value })}
        inputProps={{
          min: 1,
          max: MAX_QUANTITY,
          "aria-label": `Quantity for item ${index + 1}`,
        }}
        // The unit is what the reseller is counting, so it is stated on the
        // field itself rather than once at the top of the page.
        helperText={selected === null ? "Pick an item first." : `Counted ${unit}.`}
      />

      <IconButton
        aria-label={`Remove item ${index + 1}`}
        disabled={disabled || !canRemove}
        onClick={onRemove}
        sx={{ mt: 0.5 }}
      >
        <Trash2 size={16} />
      </IconButton>
    </Box>
  );
}

export function RequestQuotePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState<number | null>(null);
  const [lines, setLines] = useState<QuoteLineDraft[]>(() => [blankLine()]);
  const [note, setNote] = useState("");

  const catalogQuery = useQuery({
    queryKey: ["quote-catalog"],
    queryFn: fetchQuoteCatalog,
    staleTime: 60_000,
  });
  // Book order, so the grouped picker prints each category heading once.
  const catalog = useMemo(() => orderByCategory(catalogQuery.data ?? []), [catalogQuery.data]);

  // The reseller's own customers. One page of 25 is the list endpoint's own
  // bound; searching is what reaches past it.
  const [customerSearch, setCustomerSearch] = useState("");
  const customersQuery = useQuery({
    queryKey: keys.customers({ page: 1, search: customerSearch }),
    queryFn: () => listCustomers({ page: 1, search: customerSearch }),
  });
  const customers: ResellerCustomer[] = customersQuery.data?.customers ?? [];
  const selectedCustomer = customers.find((c) => c.id === customerId) ?? null;

  const request = useMutation({
    mutationFn: () =>
      requestXopQuote({
        customerId: customerId as number,
        items: toRequestItems(lines),
        ...(note.trim() !== "" ? { note: note.trim() } : {}),
      }),
    onSuccess: ({ quote }) => {
      // The list and the overview's "waiting on you" both change the moment a
      // quote is issued; invalidating here is what stops the reseller going
      // Back to a list that does not contain what it just requested. The
      // PREFIX, not one filter's key: every cached list is stale now.
      void queryClient.invalidateQueries({ queryKey: ["xop-quotes"] });
      void queryClient.invalidateQueries({ queryKey: keys.overview });
      navigate(`/quotes/${quote.id}`);
    },
  });

  if (isPermissionDenied(request.error)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Request a quote" />
        <NoAccessPanel permission={request.error.permission ?? "invoices:edit"} />
      </Box>
    );
  }

  const blocker = submitBlocker(customerId, lines);
  const canSubmit = blocker === null && !request.isPending;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Request a quote"
        description="Tell XOP Networks which customer it is for and what you need. XOP prices it and issues the quote — it is valid for 30 days."
      />

      {request.error && !isPermissionDenied(request.error) && (
        <Alert severity="error" data-testid="request-quote-error">
          {(request.error as ApiError).message}
        </Alert>
      )}

      {!catalogQuery.isPending && catalog.length === 0 && (
        <Alert severity="info" data-testid="request-quote-empty">
          XOP Networks has not published a catalogue yet, so there is nothing to request from.
          Ask your XOP Networks contact.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
            Who is this quote for?
          </Typography>
          <Autocomplete
            size="small"
            options={customers}
            value={selectedCustomer}
            loading={customersQuery.isPending}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_e, value, reason) => {
              if (reason === "input") setCustomerSearch(value);
            }}
            onChange={(_e, option) => setCustomerId(option?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...(params as TextFieldProps)}
                label="Customer"
                placeholder="Search your customers…"
                inputProps={{ ...params.inputProps, "aria-label": "Customer" }}
                helperText="One of your own customers. XOP quotes you; you quote them."
              />
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
            What do you need?
          </Typography>

          {catalogQuery.isPending ? (
            <>
              <Skeleton variant="rounded" height={56} />
              <Skeleton variant="rounded" height={56} />
            </>
          ) : (
            lines.map((line, index) => (
              <LineRow
                key={line.key}
                line={line}
                index={index}
                catalog={catalog}
                disabled={request.isPending}
                canRemove={lines.length > 1}
                onChange={(next) =>
                  setLines((prev) => prev.map((l) => (l.key === line.key ? next : l)))
                }
                onRemove={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
              />
            ))
          )}

          <Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Plus size={15} />}
              disabled={request.isPending || catalog.length === 0}
              onClick={() => setLines((prev) => [...prev, blankLine()])}
            >
              Add item
            </Button>
          </Box>

          <TextField
            multiline
            minRows={3}
            size="small"
            label="Note for XOP Networks (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 2000, "aria-label": "Note for XOP Networks" }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<FileText size={16} />}
              disabled={!canSubmit}
              onClick={() => request.mutate()}
            >
              {request.isPending ? "Generating…" : "Generate quote"}
            </Button>
            {blocker && (
              <Typography data-testid="request-quote-blocker" sx={{ fontSize: 13, color: "text.secondary" }}>
                {blocker}
              </Typography>
            )}
          </Box>

          <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>
            XOP Networks prices the quote from its own catalogue. It is valid for 30 days and its
            terms cannot be changed — to ask for something different, send another request.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default RequestQuotePage;
